<div class="session-history">
  <div class="history-header">
    <h2>Sessions</h2>
    <button (click)="refreshCurrentTab()" class="btn btn-refresh">
      Refresh
    </button>
  </div>
  <p *ngIf="error" class="error">{{ error }}</p>
  <p *ngIf="infoMessage" class="info">{{ infoMessage }}</p>

  <div class="session-controls">
    <div class="session-tabs">
      <button
        class="session-tab"
        [class.active]="activeTab === 'active'"
        (click)="selectTab('active')"
      >
        Active
      </button>
      <button
        class="session-tab"
        [class.active]="activeTab === 'completed'"
        (click)="selectTab('completed')"
      >
        Completed
      </button>
    </div>

    <div
      class="session-filters"
      *ngIf="activeTab === 'active' && canShowEligibleToggle()"
    >
      <button
        type="button"
        class="session-filter-toggle"
        [class.active]="showEligibleOnly"
        [attr.aria-pressed]="showEligibleOnly"
        (click)="toggleEligibleOnly()"
      >
        <span class="session-filter-toggle__label">Eligible for me only</span>
        <span class="session-filter-toggle__track" aria-hidden="true">
          <span class="session-filter-toggle__thumb"></span>
        </span>
      </button>
    </div>
  </div>

  <ng-container *ngIf="activeTab === 'active'">
    <div *ngIf="loading" class="loading">Loading sessions...</div>

    <div
      *ngIf="
        !loading &&
        hostedSessions.length === 0 &&
        joinedSessions.length === 0 &&
        publicSessions.length === 0
      "
      class="app-empty-state"
    >
      <svg
        class="app-empty-state__icon"
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
      <h3 class="app-empty-state__title">No sessions yet</h3>
      <p class="app-empty-state__body">
        Start a new session to begin tracking your laps.
      </p>
    </div>

    <div *ngIf="!loading && hostedSessions.length > 0" class="session-group">
      <h3 class="group-title">Hosted by you</h3>
      <div class="sessions-list">
        <div *ngFor="let session of hostedSessions" class="session-card">
          <div class="session-header" (click)="toggleSession(session)">
            <div class="session-info">
              <div class="session-title">
                <h3>{{ session.name }}</h3>
                <span
                  class="session-status"
                  [class.completed]="
                    getSessionStateLabel(session) === 'Completed'
                  "
                  [class.scheduled]="
                    getSessionStateLabel(session) === 'Scheduled'
                  "
                  [class.live]="getSessionStateLabel(session) === 'Live'"
                >
                  {{ getSessionStateLabel(session) }}
                </span>
                <span
                  class="session-visibility"
                  [class.public]="session.isPublic"
                  [class.private]="!session.isPublic"
                >
                  {{ session.isPublic ? "Public" : "Team Members Only" }}
                </span>
                <span
                  class="session-irating-chip"
                  *ngIf="hasIRatingWindow(session)"
                >
                  iRating {{ formatIRatingWindow(session) }}
                </span>
                <span
                  class="session-team-badge"
                  *ngIf="hasTeamOnlyBranding(session)"
                >
                  <span class="session-team-avatar">
                    <img
                      *ngIf="session.teamLogoUrl; else hostedTeamInitial"
                      [src]="session.teamLogoUrl"
                      alt="Team logo"
                    />
                    <ng-template #hostedTeamInitial>
                      <span>{{ getTeamInitial(session) }}</span>
                    </ng-template>
                  </span>
                  {{ getTeamLabel(session) }}
                </span>
              </div>
              <div class="session-meta">
                <span class="meta-item">
                  <strong>Host:</strong> {{ getHostName(session) }}
                </span>
                <span class="meta-item">
                  <strong>Track:</strong> {{ session.trackName }}
                </span>
                <span class="meta-item">
                  <strong>Date:</strong>
                  {{ formatDate(session.sessionDetails.startTime) }}
                </span>
                <span class="meta-item">
                  <strong>Duration:</strong>
                  {{
                    formatDuration(
                      session.sessionDetails.startTime,
                      session.sessionDetails.endTime
                    )
                  }}
                </span>
              </div>
              <div class="session-stats">
                <div class="stat">
                  <span class="stat-label">Total Laps:</span>
                  <span class="stat-value">{{ session.lapsCompleted }}</span>
                </div>
                <div class="stat" *ngIf="session.bestLap">
                  <span class="stat-label">Best Lap:</span>
                  <span class="stat-value">{{
                    formatTime(session.bestLap.lapTime)
                  }}</span>
                </div>
                <div
                  class="stat"
                  *ngIf="
                    session.sessionPlayers && session.sessionPlayers.length > 1
                  "
                >
                  <span class="stat-label">Drivers:</span>
                  <span class="stat-value">{{
                    session.sessionPlayers.length
                  }}</span>
                </div>
              </div>
              <div class="session-actions">
                <button
                  class="btn btn-share"
                  (click)="viewRaceOverview(session, $event)"
                  title="Open race snapshot"
                  *ngIf="session.status === 'completed'"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <path d="M8 15l2.5-2.5L13 15l3-3 3 3"></path>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  </svg>
                  Snapshot
                </button>
                <button
                  class="btn btn-copy"
                  (click)="copySessionSnapshotLink(session, $event)"
                  title="Copy shareable snapshot link"
                >
                  Copy Link
                </button>
                <button
                  class="btn btn-refresh"
                  (click)="openSession(session, $event)"
                  [disabled]="
                    joiningSessionId === session.id ||
                    deletingSessionId === session.id
                  "
                >
                  {{ getOpenButtonLabel(session) }}
                </button>
                <button
                  *ngIf="canDeleteSession(session)"
                  class="btn btn-delete"
                  (click)="deleteSession(session, $event)"
                  [disabled]="deletingSessionId === session.id"
                >
                  {{
                    deletingSessionId === session.id
                      ? "Deleting..."
                      : "Delete Session"
                  }}
                </button>
              </div>
            </div>
            <div class="expand-icon">
              <span>{{ session.expanded ? "▼" : "▶" }}</span>
            </div>
          </div>

          <div *ngIf="session.expanded" class="session-details">
            <!-- Stints Section -->
            <div
              *ngIf="session.stints && session.stints.length > 0"
              class="stints-section"
            >
              <h4>Stints</h4>
              <div class="stints-list">
                <div *ngFor="let stint of session.stints" class="stint-item">
                  <div class="stint-header">
                    <span class="stint-player">{{ stint.playerName }}</span>
                    <span class="stint-time">
                      {{ formatDate(stint.startTime) }}
                      <span *ngIf="stint.endTime">
                        - {{ formatDate(stint.endTime) }}</span
                      >
                    </span>
                    <span
                      class="stint-status"
                      [class.active]="stint.status === 'active'"
                    >
                      {{ stint.status }}
                    </span>
                  </div>
                  <div class="stint-stats">
                    <span>Laps: {{ getLapsForStint(session, stint.id) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Laps Section -->
            <div class="laps-section">
              <h4>All Laps ({{ session.allLaps.length }})</h4>
              <div class="laps-table-container">
                <table class="laps-table">
                  <thead>
                    <tr>
                      <th>Lap</th>
                      <th
                        *ngIf="
                          session.sessionPlayers &&
                          session.sessionPlayers.length > 1
                        "
                      >
                        Driver
                      </th>
                      <th>Time</th>
                      <th
                        *ngFor="
                          let sectorIndex of getSessionSectorIndexes(session)
                        "
                      >
                        S{{ sectorIndex + 1 }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      *ngFor="let lap of session.allLaps"
                      [class.best-lap]="
                        session.bestLap &&
                        lap.lapNumber === session.bestLap.lapNumber
                      "
                    >
                      <td>{{ lap.lapNumber }}</td>
                      <td
                        *ngIf="
                          session.sessionPlayers &&
                          session.sessionPlayers.length > 1
                        "
                      >
                        {{ lap.playerName || "-" }}
                      </td>
                      <td>
                        {{ formatTime(lap.lapTime) }}
                        <span
                          *ngIf="
                            session.bestLap &&
                            lap.lapNumber === session.bestLap.lapNumber
                          "
                          class="best-badge"
                          >Best</span
                        >
                      </td>
                      <td
                        *ngFor="
                          let sectorIndex of getSessionSectorIndexes(session)
                        "
                      >
                        {{ getLapSectorTime(lap, sectorIndex) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="!loading && joinedSessions.length > 0" class="session-group">
      <h3 class="group-title">Joined by you</h3>
      <div class="sessions-list">
        <div *ngFor="let session of joinedSessions" class="session-card">
          <div class="session-header" (click)="toggleSession(session)">
            <div class="session-info">
              <div class="session-title">
                <h3>{{ session.name }}</h3>
                <span
                  class="session-status"
                  [class.completed]="
                    getSessionStateLabel(session) === 'Completed'
                  "
                  [class.scheduled]="
                    getSessionStateLabel(session) === 'Scheduled'
                  "
                  [class.live]="getSessionStateLabel(session) === 'Live'"
                >
                  {{ getSessionStateLabel(session) }}
                </span>
                <span
                  class="session-visibility"
                  [class.public]="session.isPublic"
                  [class.private]="!session.isPublic"
                >
                  {{ session.isPublic ? "Public" : "Team Members Only" }}
                </span>
                <span
                  class="session-irating-chip"
                  *ngIf="hasIRatingWindow(session)"
                >
                  iRating {{ formatIRatingWindow(session) }}
                </span>
                <span
                  class="session-team-badge"
                  *ngIf="hasTeamOnlyBranding(session)"
                >
                  <span class="session-team-avatar">
                    <img
                      *ngIf="session.teamLogoUrl; else joinedTeamInitial"
                      [src]="session.teamLogoUrl"
                      alt="Team logo"
                    />
                    <ng-template #joinedTeamInitial>
                      <span>{{ getTeamInitial(session) }}</span>
                    </ng-template>
                  </span>
                  {{ getTeamLabel(session) }}
                </span>
              </div>
              <div class="session-meta">
                <span class="meta-item">
                  <strong>Host:</strong> {{ getHostName(session) }}
                </span>
                <span class="meta-item">
                  <strong>Track:</strong> {{ session.trackName }}
                </span>
                <span class="meta-item">
                  <strong>Date:</strong>
                  {{ formatDate(session.sessionDetails.startTime) }}
                </span>
                <span class="meta-item">
                  <strong>Duration:</strong>
                  {{
                    formatDuration(
                      session.sessionDetails.startTime,
                      session.sessionDetails.endTime
                    )
                  }}
                </span>
              </div>
              <div class="session-stats">
                <div class="stat">
                  <span class="stat-label">Total Laps:</span>
                  <span class="stat-value">{{ session.lapsCompleted }}</span>
                </div>
                <div class="stat" *ngIf="session.bestLap">
                  <span class="stat-label">Best Lap:</span>
                  <span class="stat-value">{{
                    formatTime(session.bestLap.lapTime)
                  }}</span>
                </div>
                <div
                  class="stat"
                  *ngIf="
                    session.sessionPlayers && session.sessionPlayers.length > 1
                  "
                >
                  <span class="stat-label">Drivers:</span>
                  <span class="stat-value">{{
                    session.sessionPlayers.length
                  }}</span>
                </div>
              </div>
              <div class="session-actions">
                <button
                  class="btn btn-share"
                  (click)="viewRaceOverview(session, $event)"
                  title="Open race snapshot"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <path d="M8 15l2.5-2.5L13 15l3-3 3 3"></path>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  </svg>
                  Snapshot
                </button>
                <button
                  class="btn btn-copy"
                  (click)="copySessionSnapshotLink(session, $event)"
                  title="Copy shareable snapshot link"
                >
                  Copy Link
                </button>
                <button
                  class="btn btn-refresh"
                  (click)="openSession(session, $event)"
                  [disabled]="joiningSessionId === session.id"
                >
                  {{ getOpenButtonLabel(session) }}
                </button>
              </div>
            </div>
            <div class="expand-icon">
              <span>{{ session.expanded ? "▼" : "▶" }}</span>
            </div>
          </div>

          <div *ngIf="session.expanded" class="session-details">
            <!-- Stints Section -->
            <div
              *ngIf="session.stints && session.stints.length > 0"
              class="stints-section"
            >
              <h4>Stints</h4>
              <div class="stints-list">
                <div *ngFor="let stint of session.stints" class="stint-item">
                  <div class="stint-header">
                    <span class="stint-player">{{ stint.playerName }}</span>
                    <span class="stint-time">
                      {{ formatDate(stint.startTime) }}
                      <span *ngIf="stint.endTime">
                        - {{ formatDate(stint.endTime) }}</span
                      >
                    </span>
                    <span
                      class="stint-status"
                      [class.active]="stint.status === 'active'"
                    >
                      {{ stint.status }}
                    </span>
                  </div>
                  <div class="stint-stats">
                    <span>Laps: {{ getLapsForStint(session, stint.id) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Laps Section -->
            <div class="laps-section">
              <h4>All Laps ({{ session.allLaps.length }})</h4>
              <div class="laps-table-container">
                <table class="laps-table">
                  <thead>
                    <tr>
                      <th>Lap</th>
                      <th
                        *ngIf="
                          session.sessionPlayers &&
                          session.sessionPlayers.length > 1
                        "
                      >
                        Driver
                      </th>
                      <th>Time</th>
                      <th
                        *ngFor="
                          let sectorIndex of getSessionSectorIndexes(session)
                        "
                      >
                        S{{ sectorIndex + 1 }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      *ngFor="let lap of session.allLaps"
                      [class.best-lap]="
                        session.bestLap &&
                        lap.lapNumber === session.bestLap.lapNumber
                      "
                    >
                      <td>{{ lap.lapNumber }}</td>
                      <td
                        *ngIf="
                          session.sessionPlayers &&
                          session.sessionPlayers.length > 1
                        "
                      >
                        {{ lap.playerName || "-" }}
                      </td>
                      <td>
                        {{ formatTime(lap.lapTime) }}
                        <span
                          *ngIf="
                            session.bestLap &&
                            lap.lapNumber === session.bestLap.lapNumber
                          "
                          class="best-badge"
                          >Best</span
                        >
                      </td>
                      <td
                        *ngFor="
                          let sectorIndex of getSessionSectorIndexes(session)
                        "
                      >
                        {{ getLapSectorTime(lap, sectorIndex) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="!loading && publicSessions.length > 0" class="session-group">
      <h3 class="group-title">Public Sessions</h3>
      <div class="sessions-list">
        <div *ngFor="let session of publicSessions" class="session-card">
          <div class="session-header" (click)="toggleSession(session)">
            <div class="session-info">
              <div class="session-title">
                <h3>{{ session.name }}</h3>
                <span
                  class="session-status"
                  [class.completed]="
                    getSessionStateLabel(session) === 'Completed'
                  "
                  [class.scheduled]="
                    getSessionStateLabel(session) === 'Scheduled'
                  "
                  [class.live]="getSessionStateLabel(session) === 'Live'"
                >
                  {{ getSessionStateLabel(session) }}
                </span>
                <span
                  class="session-visibility"
                  [class.public]="session.isPublic"
                  [class.private]="!session.isPublic"
                >
                  {{ session.isPublic ? "Public" : "Team Members Only" }}
                </span>
                <span
                  class="session-irating-chip"
                  *ngIf="hasIRatingWindow(session)"
                >
                  iRating {{ formatIRatingWindow(session) }}
                </span>
                <span
                  class="session-team-badge"
                  *ngIf="hasTeamOnlyBranding(session)"
                >
                  <span class="session-team-avatar">
                    <img
                      *ngIf="session.teamLogoUrl; else publicTeamInitial"
                      [src]="session.teamLogoUrl"
                      alt="Team logo"
                    />
                    <ng-template #publicTeamInitial>
                      <span>{{ getTeamInitial(session) }}</span>
                    </ng-template>
                  </span>
                  {{ getTeamLabel(session) }}
                </span>
              </div>
              <div class="session-meta">
                <span class="meta-item">
                  <strong>Host:</strong> {{ getHostName(session) }}
                </span>
                <span class="meta-item">
                  <strong>Track:</strong> {{ session.trackName }}
                </span>
                <span class="meta-item">
                  <strong>Date:</strong>
                  {{ formatDate(session.sessionDetails.startTime) }}
                </span>
                <span class="meta-item">
                  <strong>Duration:</strong>
                  {{
                    formatDuration(
                      session.sessionDetails.startTime,
                      session.sessionDetails.endTime
                    )
                  }}
                </span>
              </div>
              <div class="session-stats">
                <div class="stat">
                  <span class="stat-label">Total Laps:</span>
                  <span class="stat-value">{{ session.lapsCompleted }}</span>
                </div>
                <div class="stat" *ngIf="session.bestLap">
                  <span class="stat-label">Best Lap:</span>
                  <span class="stat-value">{{
                    formatTime(session.bestLap.lapTime)
                  }}</span>
                </div>
                <div
                  class="stat"
                  *ngIf="
                    session.sessionPlayers && session.sessionPlayers.length > 1
                  "
                >
                  <span class="stat-label">Drivers:</span>
                  <span class="stat-value">{{
                    session.sessionPlayers.length
                  }}</span>
                </div>
              </div>
              <div class="session-actions">
                <button
                  class="btn btn-share"
                  (click)="viewRaceOverview(session, $event)"
                  title="Open race snapshot"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <path d="M8 15l2.5-2.5L13 15l3-3 3 3"></path>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  </svg>
                  Snapshot
                </button>
                <button
                  class="btn btn-copy"
                  (click)="copySessionSnapshotLink(session, $event)"
                  title="Copy shareable snapshot link"
                >
                  Copy Link
                </button>
                <button
                  class="btn btn-refresh"
                  (click)="openSession(session, $event)"
                  [disabled]="
                    joiningSessionId === session.id ||
                    deletingSessionId === session.id
                  "
                >
                  {{ getOpenButtonLabel(session) }}
                </button>
                <button
                  *ngIf="canDeleteSession(session)"
                  class="btn btn-delete"
                  (click)="deleteSession(session, $event)"
                  [disabled]="deletingSessionId === session.id"
                >
                  {{
                    deletingSessionId === session.id
                      ? "Deleting..."
                      : "Delete Session"
                  }}
                </button>
              </div>
            </div>
            <div class="expand-icon">
              <span>{{ session.expanded ? "▼" : "▶" }}</span>
            </div>
          </div>

          <div *ngIf="session.expanded" class="session-details">
            <!-- Stints Section -->
            <div
              *ngIf="session.stints && session.stints.length > 0"
              class="stints-section"
            >
              <h4>Stints</h4>
              <div class="stints-list">
                <div *ngFor="let stint of session.stints" class="stint-item">
                  <div class="stint-header">
                    <span class="stint-player">{{ stint.playerName }}</span>
                    <span class="stint-time">
                      {{ formatDate(stint.startTime) }}
                      <span *ngIf="stint.endTime">
                        - {{ formatDate(stint.endTime) }}</span
                      >
                    </span>
                    <span
                      class="stint-status"
                      [class.active]="stint.status === 'active'"
                    >
                      {{ stint.status }}
                    </span>
                  </div>
                  <div class="stint-stats">
                    <span>Laps: {{ getLapsForStint(session, stint.id) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Laps Section -->
            <div class="laps-section">
              <h4>All Laps ({{ session.allLaps.length }})</h4>
              <div class="laps-table-container">
                <table class="laps-table">
                  <thead>
                    <tr>
                      <th>Lap</th>
                      <th
                        *ngIf="
                          session.sessionPlayers &&
                          session.sessionPlayers.length > 1
                        "
                      >
                        Driver
                      </th>
                      <th>Time</th>
                      <th
                        *ngFor="
                          let sectorIndex of getSessionSectorIndexes(session)
                        "
                      >
                        S{{ sectorIndex + 1 }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      *ngFor="let lap of session.allLaps"
                      [class.best-lap]="
                        session.bestLap &&
                        lap.lapNumber === session.bestLap.lapNumber
                      "
                    >
                      <td>{{ lap.lapNumber }}</td>
                      <td
                        *ngIf="
                          session.sessionPlayers &&
                          session.sessionPlayers.length > 1
                        "
                      >
                        {{ lap.playerName || "-" }}
                      </td>
                      <td>
                        {{ formatTime(lap.lapTime) }}
                        <span
                          *ngIf="
                            session.bestLap &&
                            lap.lapNumber === session.bestLap.lapNumber
                          "
                          class="best-badge"
                          >Best</span
                        >
                      </td>
                      <td
                        *ngFor="
                          let sectorIndex of getSessionSectorIndexes(session)
                        "
                      >
                        {{ getLapSectorTime(lap, sectorIndex) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="activeTab === 'completed'">
    <div *ngIf="completedLoading" class="loading">
      Loading completed sessions...
    </div>

    <div
      *ngIf="!completedLoading && completedSessions.length === 0"
      class="app-empty-state"
    >
      <svg
        class="app-empty-state__icon"
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
      <h3 class="app-empty-state__title">No completed sessions yet</h3>
      <p class="app-empty-state__body">
        Completed sessions you hosted or participated in will appear here.
      </p>
    </div>

    <div
      *ngIf="!completedLoading && completedSessions.length > 0"
      class="sessions-list"
    >
      <div
        *ngFor="let session of completedSessions"
        class="session-card completed-session-card-container"
      >
        <div
          class="session-header completed-session-header"
          (click)="openCompletedSession(session)"
        >
          <div class="session-info">
            <div class="session-title">
              <h3>{{ session.name }}</h3>
              <span class="session-status completed">Completed</span>
              <span
                class="completed-role-badge"
                [class.hosted]="session.userRole === 'HOST'"
                [class.participated]="session.userRole === 'PARTICIPANT'"
              >
                {{ getCompletedRoleLabel(session) }}
              </span>
            </div>
            <div class="session-meta">
              <span class="meta-item">
                <strong>Track:</strong> {{ session.trackName }}
              </span>
              <span class="meta-item">
                <strong>Car Class:</strong> {{ session.carClass }}
              </span>
              <span class="meta-item">
                <strong>Started:</strong> {{ formatDate(session.startedAt) }}
              </span>
              <span class="meta-item">
                <strong>Ended:</strong> {{ formatDate(session.endedAt) }}
              </span>
              <span class="meta-item">
                <strong>Hosted by:</strong> {{ session.host.name }}
              </span>
              <span class="meta-item">
                <strong>Participants:</strong> {{ session.participantCount }}
              </span>
            </div>
            <div class="session-actions">
              <button
                class="btn btn-share"
                (click)="viewCompletedRaceOverview(session, $event)"
                title="Open race snapshot"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  <path d="M8 15l2.5-2.5L13 15l3-3 3 3"></path>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                </svg>
                Snapshot
              </button>
              <button
                class="btn btn-copy"
                (click)="copyCompletedSnapshotLink(session, $event)"
                title="Copy shareable snapshot link"
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="completedPagination.hasMore" class="completed-more">
      <button
        class="btn btn-refresh"
        (click)="loadCompletedSessions(true)"
        [disabled]="completedLoading"
      >
        {{ completedLoading ? "Loading..." : "Load More" }}
      </button>
    </div>
  </ng-container>
</div>
